import { useCallback, useReducer, useEffect, ChangeEvent } from "react";
import { AttributeType, AttributeFormatDc, GeometryType } from "@evergis/api";

import { useUpdateAttributeFormat } from "./useUpdateAttributeFormat";
import { useAttributeFormatLabel } from "./useAttributeFormatLabel";
import { EditGeometryType } from "../../../../../evergis/core/attributes/geometryTypes";

import {
  reducer,
  getPointFormatSettings,
  getDateFormatSettings,
  getNumberFormatSettings,
  UPDATE_FORM_STATE,
  SET_FORM_STATE,
} from "../AttributeFormat/FormStateSettings";

import { UpdateAttribute } from "../../../../../hooks/attributes/useAttributes";
import { LayerAttribute } from "../../../EditResource/components/Layer/LayerAttributeItem";

const initialState = {
  format: "",
  rounding: null,
};

export type NumberFormState = {
  format?: string;
  rounding?: number | null;
  label?: string;
  splitDigitGroup?: boolean;
};

export type DateFormState = {
  format?: string;
  timeFormat?: string;
  showTime?: boolean;
  showTimeZone?: boolean;
};

export type PointFormState = {
  format?: string;
  rounding?: number | null;
};

export type FormStateType = NumberFormState | DateFormState | PointFormState;

// eslint-disable-next-line max-lines-per-function
export const useAttributeFormat = (
  type: AttributeType,
  id: string,
  geometryType: GeometryType | EditGeometryType,
  updateAttribute: UpdateAttribute<LayerAttribute>,
  stringFormat?: AttributeFormatDc,
  isCalculated?: boolean,
) => {
  const [formState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    dispatch({ type: UPDATE_FORM_STATE, payload: initialState });
  }, [geometryType, type]);

  useUpdateAttributeFormat(type, id, formState, updateAttribute);

  const currentFormat = useAttributeFormatLabel(formState, type);

  useEffect(() => {
    if (!(formState && formState.format)) {
      let payload = null;
      const { rounding, splitDigitGroup, label } = formState as NumberFormState;

      switch (type) {
        case AttributeType.Point:
          payload = getPointFormatSettings(stringFormat);
          break;
        case AttributeType.DateTime:
          payload = getDateFormatSettings(stringFormat);
          break;
        default:
          if (!rounding && !splitDigitGroup && !label) {
            payload = getNumberFormatSettings(geometryType, stringFormat, isCalculated);
          }
          break;
      }

      payload && dispatch({ type: SET_FORM_STATE, payload });
    }
  }, [type, isCalculated, geometryType, stringFormat && stringFormat.format, formState && formState.format, dispatch]);

  const onChangeFormatLabel = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: UPDATE_FORM_STATE, payload: { label: evt.target.value } });
    },
    [dispatch],
  );

  const onChangeFormatValue = useCallback(
    ([{ value }]) => {
      dispatch({ type: UPDATE_FORM_STATE, payload: { format: value } });
    },
    [dispatch],
  );

  const onChangeFormatTime = useCallback(
    ([{ value }]) => {
      dispatch({ type: UPDATE_FORM_STATE, payload: { timeFormat: value } });
    },
    [dispatch],
  );

  const onChangeNumberInput = useCallback(
    (value: number) => {
      dispatch({ type: UPDATE_FORM_STATE, payload: { rounding: value } });
    },
    [dispatch],
  );

  const toggleSelected = useCallback(
    (name: string) => {
      dispatch({ type: UPDATE_FORM_STATE, payload: { [name]: !formState[name] } });
    },
    [dispatch, formState],
  );

  return {
    formState,
    currentFormat,
    onChangeFormatValue,
    onChangeFormatTime,
    onChangeNumberInput,
    onChangeFormatLabel,
    toggleSelected,
  };
};
